import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  QIButton,
  QICustomSelect,
  QIInput,
  QIRichTextEditor,
} from "../../../../components";
import {
  useGetUserGroupsQuery,
  useCreateEventNotificationConfigurationMutation,
  useUpdateEventNotificationConfigurationMutation,
  useGetEmailNotificationVariablesQuery,
  useGetSystemEmailNotificationVariablesQuery,
} from "../../services";
import { SvgIcon } from "../../../Shared";
import { getLocalizedString } from "../../../../shared/translation";
import { CurrentUser } from "../../../../shared/currentUser";
import { FMDPStorage, parseServerError } from "../../../../shared/helper";
import { toAvailableVariables } from "./availableVariables";
import { AvailableVariablesModal } from "./AvailableVariablesModal";
import {
  mapConfigToValues,
  getDefaultEventEmailValues,
  validateEventEmail,
  buildEventEmailPayload,
  TIMING_OPTIONS,
} from "./eventEmailUtils";

// Connectivity ceiling for a create/update request. A request that stays
// unresolved past this window is treated as a connectivity failure.
const SUBMIT_TIMEOUT_MS = 30000;

// RTK Query surfaces network/connectivity problems via these status values.
const CONNECTIVITY_STATUSES = ["FETCH_ERROR", "TIMEOUT_ERROR"];

// Timing radio choices, in display order. Values match the backend contract.
const TIMING_CHOICES = [
  {
    value: TIMING_OPTIONS.TRIGGERED,
    labelKey: "event_email_timing_triggered",
    fallback: "When the rule is triggered",
  },
  {
    value: TIMING_OPTIONS.UNTRIGGERED,
    labelKey: "event_email_timing_untriggered",
    fallback: "When the rule is untriggered",
  },
  {
    value: TIMING_OPTIONS.BOTH,
    labelKey: "event_email_timing_both",
    fallback: "On both trigger and untrigger",
  },
];

/**
 * The "Event Email Delivery" card on the Email Notification Configuration page.
 *
 * The card decides between POST (create) and PUT (update) based on whether a
 * configuration already exists for the current client: the parent fetches the
 * existing record (GET) and passes it as `config`. When `config` is null the
 * form is empty and submit creates via POST; when `config` has an id, the form
 * is pre-filled and submit updates via PUT. Every create/update request is
 * scoped by the current client's `client_id` query param.
 *
 * Props:
 * @param {object|null} config the existing record for the client, or null
 * @param {object|null} loadError the error object from the fetch, or null
 */
export const EventEmailDeliveryCard = ({ config = null, loadError = null }) => {
  const dropdownParams = { per_page: 1000, order_by: "name" };

  const [editMode, setEditMode] = useState(false);
  const [showVariablesModal, setShowVariablesModal] = useState(false);

  const currentUser = new CurrentUser("Email Notification Configurations");

  // The current client scopes every request (client_id query param).
  const clientId = FMDPStorage.get("selected-client-id");
  const variableQuery = { client_id: clientId };
  const { data: clientVariables, isLoading: loadingClientVariables, isError: clientVariablesError } =
    useGetEmailNotificationVariablesQuery(variableQuery, { skip: !clientId || !showVariablesModal });
  const { data: systemVariables, isLoading: loadingSystemVariables, isError: systemVariablesError } =
    useGetSystemEmailNotificationVariablesQuery(variableQuery, { skip: !clientId || !showVariablesModal });
  const availableVariables = [
    ...toAvailableVariables(systemVariables?.data, "System"),
    ...toAvailableVariables(clientVariables?.data, "Client"),
  ];

  // Fetch user groups for the recipient control. RTK Query caches the request.
  const { data: userGroups, isSuccess: isUserGroupsSuccess } =
    useGetUserGroupsQuery(dropdownParams);

  // Create / update mutations against event_notification_configurations.
  const [createConfig, { isLoading: isCreating }] =
    useCreateEventNotificationConfigurationMutation();

  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateEventNotificationConfigurationMutation();

  // Derive the initial form values from the existing record, falling back to
  // documented defaults when the record is null or failed to load.
  const initialValues = loadError
    ? getDefaultEventEmailValues()
    : mapConfigToValues(config);

  // Persist the configuration. Validation runs first; an invalid form binds
  // field messages and blocks submission while preserving other field values.
  // On success we switch to View_Mode with a confirmation toast. Server
  // validation errors bind to fields and keep Edit_Mode; server errors and
  // network/timeout failures surface a toast and keep Edit_Mode.
  const handleSubmit = async (values, formikHelpers) => {
    const validationErrors = validateEventEmail(values);
    if (Object.keys(validationErrors).length > 0) {
      formikHelpers.setErrors(validationErrors);
      return;
    }

    const formData = buildEventEmailPayload(values);

    // POST when no record exists, PUT otherwise. Both carry client_id so the
    // backend scopes the write to the current client.
    const mutationPromise = config?.id
      ? updateConfig({ id: config.id, client_id: clientId, formData }).unwrap()
      : createConfig({ client_id: clientId, formData }).unwrap();

    // 30s connectivity ceiling: race the mutation against a timeout that
    // rejects with a synthetic connectivity error.
    let timeoutId;
    const timeoutPromise = new Promise((_resolve, reject) => {
      timeoutId = setTimeout(
        () => reject({ status: "TIMEOUT_ERROR" }),
        SUBMIT_TIMEOUT_MS
      );
    });

    try {
      await Promise.race([mutationPromise, timeoutPromise]);
      setEditMode(false);
      toast.success(
        getLocalizedString(
          "event_email_config_saved",
          "The configuration was saved."
        )
      );
    } catch (error) {
      const status = error?.status;

      if (CONNECTIVITY_STATUSES.includes(status)) {
        toast.error(
          getLocalizedString(
            "event_email_config_connectivity_error",
            "A connectivity failure occurred. Please check your connection and try again."
          )
        );
      } else if (status === 500 || status === 404) {
        toast.error(
          getLocalizedString(
            "event_email_config_error",
            "Something went wrong. Please try again later."
          )
        );
      } else {
        // Field-level validation error from the server. Bind messages to the
        // corresponding fields, stay in Edit_Mode, and keep entered values.
        formikHelpers.setErrors(parseServerError(error));
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  // On load failure, surface a non-blocking toast; the card stays usable with
  // defaults so the user can still create a configuration.
  useEffect(() => {
    if (loadError) {
      toast.error(
        getLocalizedString(
          "event_email_config_load_error",
          "The existing configuration could not be loaded."
        )
      );
    }
  }, [loadError]);

  // Derived in-flight guard consumed by the footer UPDATE action.
  const submitting = isCreating || isUpdating;

  // Available user groups feeding the recipient select. When the query
  // resolves with zero rows, surface a localized "no user groups available"
  // indication in place of the select.
  const userGroupList = userGroups?.data ?? [];
  const hasNoUserGroups = isUserGroupsSuccess && userGroupList.length === 0;

  // Enter Edit_Mode, re-checking the update permission at activation time. If
  // permission was lost, stay in View_Mode and surface a denial toast.
  const enterEditMode = () => {
    const liveUser = new CurrentUser("Email Notification Configurations");
    if (!liveUser.canUpdate) {
      toast.error(
        getLocalizedString(
          "edit_access_denied",
          "You do not have permission to edit this configuration."
        )
      );
      return;
    }
    setEditMode(true);
  };

  // CANCEL restores every field to the last-saved baseline (or the defaults
  // when no record exists) and returns to View_Mode.
  const handleCancel = () => {
    formik.resetForm({ values: mapConfigToValues(config) });
    setEditMode(false);
  };

  // Human-readable fallbacks for the validation keys produced by
  // validateEventEmail(). These keys double as localization ids; when no
  // translation is registered the fallback text is shown instead of the raw
  // snake_case key.
  const ERROR_FALLBACKS = {
    user_group_required: "Please select a recipient user group.",
    subject_required: "Subject is required.",
    subject_too_long: "Subject must be 255 characters or fewer.",
    message_required: "Message is required.",
    message_too_long: "Message must be 5000 characters or fewer.",
    timing_invalid: "Please select a valid timing option.",
  };

  // Turns a raw field error (a validation key, a server message, or undefined)
  // into a display-ready, localized string. Only fires when the field has been
  // touched so pristine fields stay clean.
  const localizeError = (field) => {
    if (!formik.touched[field] || !formik.errors[field]) return "";
    const key = formik.errors[field];
    return getLocalizedString(key, ERROR_FALLBACKS[key] || key);
  };

  return (
    <div className="customization_block">
      <header className="customization_block_header">
        <strong>
          {getLocalizedString("event_email_delivery", "Event Email Delivery")}
        </strong>
        {currentUser.canUpdate && !editMode && (
          <span className="edit">
            <SvgIcon name="edit" wrapperClass="mx-2" onClick={enterEditMode} />
          </span>
        )}
      </header>

      <div>
        {loadError && (
          <div className="text-danger mb-2">
            {getLocalizedString(
              "event_email_config_load_error",
              "The existing configuration could not be loaded."
            )}
          </div>
        )}

        {/* Notification type is hidden from the UI: "Email" is currently the
            only channel. The value is still carried in the form values and
            payload (defaulted to "email") so the backend contract is intact. */}

        {/* Recipient: a single user group (user_group_id). */}
        {hasNoUserGroups ? (
          <div className="qi-form-element">
            <label className="qi-input_label">
              {getLocalizedString("user_group", "User Group")}
            </label>
            <p className="text-muted">
              {getLocalizedString(
                "no_user_groups_available",
                "No user groups are available for selection."
              )}
            </p>
          </div>
        ) : (
          <QICustomSelect
            label={getLocalizedString("user_group", "User Group")}
            value={formik.values.user_group_id}
            onChange={(value) => formik.setFieldValue("user_group_id", value)}
            disabled={!editMode}
            readOnly={!editMode}
            error={localizeError("user_group_id")}
          >
            <li value="">
              {getLocalizedString("select_user_group", "Select User Group")}
            </li>
            {isUserGroupsSuccess &&
              userGroupList.map((group) => (
                <li key={group.id} value={group.id}>
                  {group.name}
                </li>
              ))}
          </QICustomSelect>
        )}

        {/* Subject: single-line, 0-255 chars, stored verbatim. */}
        <QIInput
          label={getLocalizedString("subject", "Subject")}
          value={formik.values.subject}
          onChange={(e) => formik.setFieldValue("subject", e.target.value)}
          readOnly={!editMode}
          disabled={!editMode}
          maxLength={255}
          error={localizeError("subject")}
          placeholder="Subject [RULE_NAME]"
        />

        {/* Message body: rich text (HTML), stored verbatim. */}
        <QIRichTextEditor
          label={getLocalizedString("message", "Message")}
          value={formik.values.message_body}
          onChange={(html) => formik.setFieldValue("message_body", html)}
          readOnly={!editMode}
          error={localizeError("message_body")}
          placeholder="ex: Vehicle: [RULE_NAME]"
        />
        {editMode && (
          <div className="mb-4" style={{ display: "flex", justifyContent: "flex-end" }}>
            <QIButton className="secondary sm" onClick={() => setShowVariablesModal(true)}>
              {getLocalizedString("available_variables", "Available Variables")}
            </QIButton>
          </div>
        )}

        {/* Timing: exactly one active choice, default "send_on_triggered". */}
        <fieldset
          className={`qi-input qi-form-element event-email-timing ${
            !editMode ? "disabled" : ""
          }`}
          disabled={!editMode}
        >
          <span className="qi-input_label" aria-hidden="true">
            {getLocalizedString("event_email_timing", "Timing")}
          </span>
          <div className="qi-input_wrapper event-email-timing_options">
            {TIMING_CHOICES.map((choice) => (
              <label key={choice.value} className="event-email-timing_option">
                <input
                  type="radio"
                  name="event_email_timing"
                  value={choice.value}
                  checked={formik.values.timing === choice.value}
                  onChange={() => formik.setFieldValue("timing", choice.value)}
                  disabled={!editMode}
                />
                <span className="event-email-timing_option-label">
                  {getLocalizedString(choice.labelKey, choice.fallback)}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {formik.errors.serverError && (
          <div className="text-danger mt-2">{formik.errors.serverError}</div>
        )}

        {editMode && (
          <div className="user-profile-details_footer pt-4">
            <QIButton className="secondary sm mr-2" onClick={handleCancel}>
              {getLocalizedString("cancel", "Cancel")}
            </QIButton>
            <QIButton
              className="primary sm"
              onClick={formik.handleSubmit}
              disabled={submitting}
            >
              {config?.id
                ? getLocalizedString("update", "Update")
                : getLocalizedString("save", "Save")}
            </QIButton>
          </div>
        )}
      </div>

      {/* Variables picker: copy a token for the subject or message field. */}
      <AvailableVariablesModal
        show={showVariablesModal}
        onHide={() => setShowVariablesModal(false)}
        variables={availableVariables}
        loading={loadingClientVariables || loadingSystemVariables}
        loadError={clientVariablesError || systemVariablesError}
      />
    </div>
  );
};

EventEmailDeliveryCard.propTypes = {
  config: PropTypes.object,
  loadError: PropTypes.object,
};

EventEmailDeliveryCard.defaultProps = {
  config: null,
  loadError: null,
};
